import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { bubbleImageSrc } from '@/lib/bubbleImage';
import { Compass as CompassIcon, MapPin, Sparkle } from '@phosphor-icons/react';

const Compass = () => {
  const [data, setData] = useState(null);
  const [bubbles, setBubbles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bubbleId, setBubbleId] = useState('');
  const [label, setLabel] = useState('');

  const load = async () => {
    try {
      const [c, mine] = await Promise.all([
        api.get('/compass'),
        api.get('/bubbles/mine'),
      ]);
      setData(c.data);
      setBubbles(mine.data.bubbles || []);
      if (mine.data.bubbles?.[0]) setBubbleId(mine.data.bubbles[0].id);
    } catch (err) { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const checkIn = async () => {
    if (!navigator.geolocation) return toast.error('no geolocation on this device');
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        await api.post('/checkin', {
          lat: pos.coords.latitude, lon: pos.coords.longitude,
          bubble_id: bubbleId, label: label || null,
        });
        toast.success('checked in.');
        load();
      } catch (err) { toast.error('could not check in'); }
    }, () => toast.error('location permission denied'), { enableHighAccuracy: true });
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 pb-24">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-widest text-bx-cyan">layer 2 · world · proximity</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black mt-1">Bubble Compass</h1>
        <p className="text-white/60 mt-2 text-sm max-w-xl">
          Opt-in. Check in with one Bubble. See whose bubbles are within a kilometre.
          When two of you are this close, your bubbles can brush in person.
        </p>
      </div>

      <div className="glass rounded-2xl p-5 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <select value={bubbleId} onChange={(e) => setBubbleId(e.target.value)}
            data-testid="compass-bubble"
            className="bg-white/5 border border-white/10 rounded-full px-3 py-2 text-xs focus:outline-none">
            {bubbles.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
          </select>
          <input
            data-testid="compass-label"
            value={label}
            onChange={(e) => setLabel(e.target.value.slice(0, 40))}
            placeholder="where are you? (optional)"
            className="flex-1 min-w-32 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-xs focus:outline-none" />
          <button data-testid="compass-checkin" onClick={checkIn}
            className="btn-volt px-5 py-2 rounded-full text-xs font-bold flex items-center gap-1">
            <MapPin size={12} weight="fill" /> Check in
          </button>
        </div>
        {data?.geohash && (
          <div className="text-[10px] uppercase tracking-widest text-white/60 mt-3">
            you · cell <span className="text-bx-cyan">{data.geohash}</span> · neighbourhood <span className="text-bx-cyan">{data.prefix5}…</span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-white/60">listening…</div>
      ) : !data?.geohash ? (
        <div className="glass rounded-3xl p-10 text-center">
          <CompassIcon size={32} className="text-bx-cyan mx-auto mb-3" />
          <div className="text-white/65">Check in once. Then the compass starts pointing.</div>
        </div>
      ) : (data.nearby || []).length === 0 ? (
        <div className="glass rounded-3xl p-10 text-center">
          <div className="text-white/65">No bubbles in your neighbourhood right now.</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {data.nearby.map((n) => {
            const src = bubbleImageSrc({ image_url: n.bubble_image_url });
            return (
              <Link key={n.user_id} to={n.bubble_id ? `/bubble/${n.bubble_id}` : `/creator/${n.username}`}
                data-testid={`nearby-${n.user_id}`}
                className="glass rounded-2xl p-4 hover:bg-white/[0.06] transition flex flex-col items-center gap-2">
                <div className="h-16 w-16 rounded-full overflow-hidden bg-white/5 ring-1 ring-white/20">
                  {src ? <img src={src} alt={n.bubble_name} className="h-full w-full object-cover breathe" /> : null}
                </div>
                <div className="text-sm font-bold text-center">{n.bubble_name || `@${n.username}`}</div>
                <div className="text-[10px] uppercase tracking-widest text-white/60">cell · {n.geohash}</div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Compass;
